import { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export function ContextChipSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const mainCardRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<SVGPathElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // ENTRANCE (0% - 30%)
      scrollTl.fromTo(
        headlineRef.current,
        { x: '-18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0
      );

      scrollTl.fromTo(
        mainCardRef.current,
        { x: '-55vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.05
      );

      scrollTl.fromTo(
        chipRef.current,
        { x: '40vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.1
      );

      // Arrow draw-on
      if (arrowRef.current) {
        const pathLength = arrowRef.current.getTotalLength();
        gsap.set(arrowRef.current, {
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength,
        });
        scrollTl.to(arrowRef.current, { strokeDashoffset: 0, ease: 'none' }, 0);
      }

      // SETTLE (30% - 70%) - hold position

      // EXIT (70% - 100%)
      scrollTl.fromTo(
        mainCardRef.current,
        { x: 0, opacity: 1 },
        { x: '-18vw', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        chipRef.current,
        { x: 0, opacity: 1 },
        { x: '18vw', opacity: 0, ease: 'power2.in' },
        0.72
      );

      scrollTl.fromTo(headlineRef.current, { opacity: 1 }, { opacity: 0, ease: 'power2.in' }, 0.75);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pinned z-30 flex items-center justify-center bg-[#F6F5F2]"
    >
      {/* Headline Group */}
      <div
        ref={headlineRef}
        className="absolute"
        style={{ left: '9vw', top: '10vh', width: '44vw' }}
      >
        <h2
          className="font-display font-bold leading-[1.05] tracking-[-0.02em] text-[#0B0D10]"
          style={{ fontSize: 'clamp(34px, 3.6vw, 52px)' }}
        >
          Context chips
        </h2>
        <p
          className="mt-4 leading-relaxed text-[#6B6F78]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)', width: '38vw' }}
        >
          Tap any quote to expand the surrounding paragraph, definitions, or effective dates—without
          leaving the card.
        </p>
      </div>

      {/* Main Quote Card */}
      <div
        ref={mainCardRef}
        className="receipt-card absolute p-6"
        style={{ left: '9vw', top: '38vh', width: '46vw', height: '44vh' }}
      >
        {/* Source Header */}
        <div className="mb-4 flex items-center gap-3 border-b border-[rgba(11,13,16,0.08)] pb-4">
          <FileText className="h-5 w-5 text-[#E1062C]" />
          <div>
            <p className="font-medium text-[#0B0D10]">Fair Labor Standards Act (as amended)</p>
            <p className="font-mono text-xs text-[#6B6F78]">U.S. Department of Labor</p>
          </div>
          <span className="ml-auto font-mono text-xs text-[#E1062C]">2009-07-24</span>
        </div>

        {/* Quote */}
        <p
          className="mb-6 font-medium leading-relaxed text-[#0B0D10]"
          style={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }}
        >
          …the minimum wage shall be $7.25 per hour, effective July 24, 2009…
        </p>

        {/* Context Toggle */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-medium text-[#E1062C] hover:underline"
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Hide context
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show surrounding text
            </>
          )}
        </button>

        {/* Expanded Context */}
        {isExpanded && (
          <div className="mt-4 rounded-xl border border-[rgba(11,13,16,0.08)] bg-[#F6F5F2] p-4">
            <p className="text-sm leading-relaxed text-[#6B6F78]">
              Section 206. Minimum wage. (a) Except as otherwise provided in this section, every
              employer shall pay to each of his employees who in any workweek is engaged in commerce
              or in the production of goods for commerce, or is employed in an enterprise engaged in
              commerce or in the production of goods for commerce, wages at the following rates…
            </p>
            <div className="mt-3 border-t border-[rgba(11,13,16,0.08)] pt-3">
              <p className="font-mono text-xs text-[#6B6F78]">
                <span className="text-[#E1062C]">Effective:</span> 2009-07-24 •
                <span className="text-[#E1062C]"> Amended by:</span> Fair Minimum Wage Act of 2007
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Context Chip */}
      <div ref={chipRef} className="absolute" style={{ left: '60vw', top: '46vh', width: '30vw' }}>
        {/* Arrow SVG */}
        <svg
          className="absolute"
          style={{ left: '-8vw', top: '2vh', width: '8vw', height: '8vh' }}
          viewBox="0 0 100 60"
          fill="none"
        >
          <path
            ref={arrowRef}
            d="M0 30 Q 50 30, 80 10"
            stroke="#E1062C"
            strokeWidth="2"
            strokeDasharray="4 2"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#E1062C" />
            </marker>
          </defs>
        </svg>

        {/* Chip Card */}
        <div className="receipt-card-sm border-dotted-context chip-pulse p-5">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#E1062C]">
            Surrounding text
          </p>
          <p className="mb-3 text-sm leading-relaxed text-[#0B0D10]">
            Section 206. Minimum wage. (a) … (b) …
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="rounded bg-[#F6F5F2] px-2 py-1 text-xs text-[#6B6F78]">
              Effective dates
            </span>
            <span className="rounded bg-[#F6F5F2] px-2 py-1 text-xs text-[#6B6F78]">
              Amendments
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
